/**
 * AJAX collection filtering via Shopify Section Rendering API.
 */
class CollectionFilters {
  #abortController = null;
  #requestId = 0;
  #debounceTimer = null;
  #debounceMs = 280;

  constructor() {
    this.root = document.querySelector('[data-collection-filters]');
    if (!this.root) return;

    this.sectionId = this.root.dataset.sectionId;
    this.#bindEvents();
    this.#updateBadgesClass();
  }

  #bindEvents() {
    this.root.addEventListener('change', (event) => {
      const target = event.target;

      if (target.matches('input[type="checkbox"]') && target.closest('#CollectionFiltersForm')) {
        this.#scheduleApply();
        return;
      }

      if (target.id === 'filter-sort-by') {
        this.#scheduleApply();
        return;
      }

      if (target.id === 'filter-by-interest') {
        this.#applyInterestFilter(target);
      }
    });

    this.root.addEventListener('submit', (event) => {
      const form = event.target;
      if (form.id !== 'CollectionFiltersForm') return;

      event.preventDefault();
      this.#scheduleApply();
    });

    this.root.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link) return;

      if (!link.closest('.collection-active-filters, .pagination, #CollectionPagination')) return;

      event.preventDefault();
      const url = new URL(link.href, window.location.origin);
      this.#fetchAndRender(`${url.pathname}${url.search}`);
    });

    window.addEventListener('popstate', () => {
      this.#fetchAndRender(window.location.pathname + window.location.search, false);
    });
  }

  #scheduleApply() {
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => this.#applyFilters(), this.#debounceMs);
  }

  #applyInterestFilter(select) {
    const targetUrl = new URL(select.value, window.location.origin);
    const params = new URLSearchParams();
    const sortSelect = this.root.querySelector('#filter-sort-by');

    if (sortSelect?.value) {
      params.set('sort_by', sortSelect.value);
    }

    const search = params.toString();
    const path = `${targetUrl.pathname}${search ? `?${search}` : ''}`;

    this.#fetchAndRender(path);
  }

  #applyFilters() {
    const params = this.#buildSearchParams();
    params.delete('page');

    const basePath = this.#getCollectionPath();
    const search = params.toString();
    const path = `${basePath}${search ? `?${search}` : ''}`;

    this.#fetchAndRender(path);
  }

  #getCollectionPath() {
    const form = document.getElementById('CollectionFiltersForm');

    if (form?.action) {
      return new URL(form.action, window.location.origin).pathname;
    }

    return window.location.pathname;
  }

  #buildSearchParams() {
    const params = new URLSearchParams();
    const form = document.getElementById('CollectionFiltersForm');

    if (form) {
      for (const checkbox of form.querySelectorAll('input[type="checkbox"]:checked')) {
        if (!checkbox.name || !checkbox.value) continue;
        params.append(checkbox.name, checkbox.value);
      }

      for (const input of form.querySelectorAll('input[type="number"], input[type="hidden"]')) {
        if (!input.name || input.type === 'checkbox') continue;
        if (input.value === '' || input.value == null) continue;

        let value = input.value;

        if (
          input.classList.contains('price-range-min') ||
          input.classList.contains('price-range-max')
        ) {
          const parsed = parseInt(value, 10);
          if (Number.isNaN(parsed)) continue;
          value = String(parsed);
        }

        if (input.name === 'sort_by') {
          params.set('sort_by', value);
        } else {
          params.set(input.name, value);
        }
      }
    }

    const sortSelect = this.root.querySelector('#filter-sort-by');
    if (sortSelect?.value) {
      params.set('sort_by', sortSelect.value);
    }

    return params;
  }

  async #fetchAndRender(path, pushState = true) {
    if (!this.sectionId) return;

    const requestId = ++this.#requestId;
    const requestUrl = new URL(path, window.location.origin);
    requestUrl.searchParams.set('section_id', this.sectionId);

    if (this.#abortController) {
      this.#abortController.abort();
    }

    this.#abortController = new AbortController();
    this.#setLoading(true);

    try {
      const response = await fetch(requestUrl.toString(), {
        signal: this.#abortController.signal,
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (requestId !== this.#requestId) return;
      if (!response.ok) {
        window.location.href = path;
        return;
      }

      const html = await response.text();
      if (requestId !== this.#requestId) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const source = this.#findSectionRoot(doc);

      if (!source) {
        window.location.href = path;
        return;
      }

      this.#updateDom(source);

      const displayUrl = new URL(path, window.location.origin);
      if (pushState) {
        history.pushState({ collectionFilters: true }, '', displayUrl.pathname + displayUrl.search);
      }

      this.#syncRootDataset(source);
      this.#updateBadgesClass(displayUrl.search);
      this.#reinitProductCards();
      this.root.dispatchEvent(new CustomEvent('collection:filtered', { bubbles: true }));
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (requestId === this.#requestId) {
        window.location.href = path;
      }
    } finally {
      if (requestId === this.#requestId) {
        this.#setLoading(false);
      }
    }
  }

  #findSectionRoot(doc) {
    const sectionId = this.sectionId;

    return (
      doc.getElementById(`shopify-section-${sectionId}`) ||
      doc.querySelector(`[id="shopify-section-${sectionId}"]`) ||
      doc.querySelector('[data-collection-filters]') ||
      doc.querySelector('.collection-page')?.parentElement ||
      doc.body.firstElementChild
    );
  }

  #updateDom(source) {
    this.#replaceElement('#CollectionProductGrid', source);
    this.#replaceElement('#CollectionActiveFilters', source);
    this.#replaceElement('#CollectionProductCount', source);
    this.#replaceElement('#CollectionPagination', source);
    this.#replaceElement('#CollectionFacetsScroll', source);
  }

  #syncRootDataset(source) {
    const nextRoot = source.querySelector('[data-collection-filters]');
    if (!nextRoot?.dataset.collectionUrl) return;

    this.root.dataset.collectionUrl = nextRoot.dataset.collectionUrl;
  }

  #reinitProductCards() {
    const forms = document.querySelectorAll(
      '.product-atc.product__form.product-form.jq-cart-add-product.product-card__form'
    );

    for (const form of forms) {
      if (form.dataset.collectionFilterBound === 'true') continue;

      form.dataset.collectionFilterBound = 'true';
      form.addEventListener('click', (event) => {
        event.stopPropagation();
      });
    }

    document.dispatchEvent(new CustomEvent('collection:updated'));

    if (typeof window.initCollectionFacetPriceRange === 'function') {
      window.initCollectionFacetPriceRange();
    }
  }

  #replaceElement(selector, source) {
    const current = document.querySelector(selector);
    const next = source.querySelector(selector);

    if (!current || !next) return;

    current.replaceWith(document.importNode(next, true));
  }

  #setLoading(isLoading) {
    const grid = document.getElementById('CollectionProductGrid');
    if (!grid) return;

    grid.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    grid.classList.toggle('collection-page__grid-products--loading', isLoading);
  }

  #updateBadgesClass(search = window.location.search) {
    const normalized = search.replace(/^\?/, '');
    const params = new URLSearchParams(normalized);
    const sortBy = params.get('sort_by');
    const hasFilters = [...params.keys()].some((key) => key.startsWith('filter.'));

    const showBadges =
      normalized === '' ||
      (params.size <= 1 && (!sortBy || sortBy === 'manual') && !hasFilters) ||
      (params.get('page') === '1' && params.size <= 2 && !hasFilters);

    document.body.classList.toggle('collection-show-badges', showBadges);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CollectionFilters());
} else {
  new CollectionFilters();
}
